import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ImmerHook, useImmer } from 'use-immer';

/**
 * Session-scoped cache for list page search filters.
 *
 * List pages unmount whenever the user opens an add/edit/detail screen, which loses the applied
 * filters, the sort and the page the user was on. Caching them in `sessionStorage` restores that
 * state on the way back, while still keeping it out of the next login on a shared machine.
 * Keys are the current route plus a name, so each list page keeps its own entries.
 */
const PREFIX = 'saw-filters:';

/** `JSON.stringify` turns Dates into ISO strings; revive them so date filters round-trip. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
const reviveDates = (_key: string, value: unknown) =>
  typeof value === 'string' && ISO_DATE.test(value) ? new Date(value) : value;

const read = <T>(key: string): T | undefined => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw === null ? undefined : (JSON.parse(raw, reviveDates) as T);
  } catch {
    return undefined;
  }
};

const write = <T>(key: string, value: T) => {
  try {
    if (value === undefined) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // best-effort persistence
  }
};

const useCacheKey = (name: string) => `${PREFIX}${useLocation().pathname}:${name}`;

const seed = <T>(key: string, initial: T | (() => T)): T => {
  const cached = read<T>(key);
  if (cached !== undefined) return cached;
  return typeof initial === 'function' ? (initial as () => T)() : initial;
};

/** `useState` that seeds itself from the filter cache and writes every change back. */
export function useCachedState<T>(
  name: string,
  initial: T | (() => T)
): [T, Dispatch<SetStateAction<T>>] {
  const key = useCacheKey(name);
  const [state, setState] = useState<T>(() => seed(key, initial));

  useEffect(() => write(key, state), [key, state]);

  return [state, setState];
}

/** `useImmer` variant of {@link useCachedState} for pagination/sort state. */
export function useCachedImmer<T>(name: string, initial: T | (() => T)): ImmerHook<T> {
  const key = useCacheKey(name);
  const [state, updateState] = useImmer<T>(() => seed(key, initial));

  useEffect(() => write(key, state), [key, state]);

  return [state, updateState];
}

/** Drops every cached list filter — used on logout so filters never cross users. */
export const clearFilterCache = () => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // nothing to clear
  }
};
